import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Pilcrow,
  Type,
} from "lucide-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import { chipFor, chipHtml, unchip } from "@/lib/templateChips";

export interface StyledTemplateEditorHandle {
  /** Insert a raw Jinja snippet (e.g. `{{ contract.price.rent }}`) as a chip at the caret. */
  insertSnippet: (raw: string) => void;
}

const CHIP_CSS = `
.tok{display:inline-block;border-radius:4px;padding:0 5px;font-size:.82em;font-weight:500;
line-height:1.5;white-space:nowrap;cursor:default;-webkit-user-select:none;user-select:none;
font-family:ui-sans-serif,system-ui,sans-serif}
.tok-var{background:#e0edff;color:#1e40af;border:1px solid #bfdbfe}
.tok-sign{background:#fef3c7;color:#92400e;border:1px solid #fde68a}
.tok-tag{background:#eef2f7;color:#475569;border:1px solid #cbd5e1;
font-family:ui-monospace,SFMono-Regular,monospace;font-size:.72em}
body{outline:none}
`;

/**
 * Faithful visual template editor. Renders the content-only body inside the real document
 * base CSS (so it looks like the printed page) and shows `{{ fields }}` / `{% logic %}` as
 * chips. Editing happens on the actual HTML (contentEditable), so classes, tables and
 * conditionals are preserved exactly; chips round-trip back to literal Jinja on read.
 */
export const StyledTemplateEditor = forwardRef<
  StyledTemplateEditorHandle,
  { initialValue: string; onChange: (html: string) => void; baseCss: string }
>(function StyledTemplateEditor({ initialValue, onChange, baseCss }, ref) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const docRef = useRef<Document | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const initialRef = useRef(initialValue);
  const cssRef = useRef(baseCss);
  cssRef.current = baseCss;

  useEffect(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    docRef.current = doc;
    doc.open();
    doc.write(
      `<!doctype html><html><head><meta charset="utf-8">${cssRef.current}` +
        `<style>${CHIP_CSS}</style></head><body>${chipHtml(initialRef.current)}</body></html>`,
    );
    doc.close();
    try {
      doc.body.contentEditable = "true";
    } catch {
      /* ignore */
    }
    const emit = () => onChangeRef.current(unchip(doc.body.innerHTML));
    doc.addEventListener("input", emit);
    return () => doc.removeEventListener("input", emit);
  }, []);

  function exec(command: string, value?: string) {
    const doc = docRef.current;
    if (!doc) return;
    doc.body.focus();
    doc.execCommand(command, false, value);
    onChangeRef.current(unchip(doc.body.innerHTML));
  }

  useImperativeHandle(ref, () => ({
    insertSnippet(raw: string) {
      const doc = docRef.current;
      if (!doc) return;
      doc.body.focus();
      // A trailing space keeps the caret outside the (atomic) chip.
      doc.execCommand("insertHTML", false, chipFor(raw) + " ");
      onChangeRef.current(unchip(doc.body.innerHTML));
    },
  }));

  const Tool = ({
    icon: Icon,
    label,
    onClick,
  }: {
    icon: typeof Bold;
    label: string;
    onClick: () => void;
  }) => (
    <button
      type="button"
      title={label}
      aria-label={label}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="rounded p-1.5 text-muted-foreground hover:bg-background hover:text-foreground"
    >
      <Icon className="size-4" />
    </button>
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-0.5 border-b border-border bg-muted px-2 py-1">
        <Tool icon={Bold} label="Bold" onClick={() => exec("bold")} />
        <Tool icon={Italic} label="Italic" onClick={() => exec("italic")} />
        <Tool icon={Type} label="Heading" onClick={() => exec("formatBlock", "<h2>")} />
        <Tool
          icon={Pilcrow}
          label="Paragraph"
          onClick={() => exec("formatBlock", "<p>")}
        />
        <Tool icon={List} label="Bullet list" onClick={() => exec("insertUnorderedList")} />
        <Tool
          icon={ListOrdered}
          label="Numbered list"
          onClick={() => exec("insertOrderedList")}
        />
        <span className="ml-2 text-xs text-muted-foreground">
          Styled editing — fields &amp; logic shown as chips
        </span>
      </div>
      <iframe
        ref={iframeRef}
        title="Styled template editor"
        className="min-h-0 flex-1 bg-white"
      />
    </div>
  );
});
